/**
 * api.ts — Einheitliches Error-Handling für alle db.*-Aufrufe
 *
 * Nutzung:
 *   const result = await api(db.createAuftrag(data), openToast, t);
 *   if (!result) return; // Fehler wurde bereits angezeigt
 *   // result enthält die Daten
 *
 * Oder ohne Toast (stille Hintergrund-Ops):
 *   const result = await apiSilent(db.getProfile(id), 'getProfile');
 *   if (!result) console.warn('Profile load failed');
 *
 * Unterstützt zwei Error-Formate:
 *   1. Supabase: { error: { message: 'Foo' } }  → generischer hcApiFehler
 *   2. Strukturiert: { error: { code: 'uploadFileTooLarge', maxMb: 10 } }
 *      → nutzt code als i18n-Key mit {placeholder}-Ersetzung
 *
 * Alle Fehler werden zusätzlich über `logError()` an das zentrale Monitoring
 * (siehe lib/monitoring) weitergeleitet — so tauchen DB-Fehler automatisch
 * im Admin-Monitoring-Dashboard auf, nicht nur in der Browser-Console.
 *
 * Der Generic `<T>` reicht den Datentyp der Eingangs-Promise durch — sobald
 * Caller migriert sind und `db.*` echte Typen liefert, weiß TypeScript
 * automatisch was am Ende rauskommt:
 *
 *   const auftrag = await api(db.createAuftrag(data));
 *   //    ^? Auftrag | null
 */

import { logError } from './monitoring.js';

// ════════════════════════════════════════════════════════════════════
// Public types
// ════════════════════════════════════════════════════════════════════

/**
 * Toast-Funktion für UI-Feedback.
 * Lokaler Helfer-Typ — bekommt später beim Toast-Modul seinen echten
 * Heimat-Typ und wird dann zentral importiert.
 */
type ToastFn = (message: string, type?: 'error' | 'info' | 'success') => void;

/**
 * i18n-Übersetzungs-Funktion.
 * Lokaler Helfer-Typ — bekommt später beim i18n-Modul seinen echten Typ.
 */
type TranslateFn = (key: string) => string;

/**
 * Fehler-Format eines `db.*`-Aufrufs.
 *
 * Zwei sich gegenseitig ausschließende Varianten:
 *   1. Supabase-Style: nur `message`
 *   2. Strukturiert:   `code` (i18n-Key) plus beliebige Platzhalter-Werte
 */
export type ApiError =
  | { message: string; code?: never }
  | { code: string; message?: string; [param: string]: unknown };

/**
 * Standard-Antwortformat aller `db.*`-Aufrufe.
 * Genau eines von `data` oder `error` ist gesetzt.
 *
 * `error` ist bewusst `unknown` statt `ApiError` — die Supabase-Module
 * geben verschiedene Error-Formate zurück (Supabase-Error, strukturierte
 * Validation-Errors, string-Errors). Die `api()`-Funktion macht intern
 * Runtime-Narrowing und erkennt alle Formate automatisch.
 */
export interface ApiResult<T> {
  data?: T | null;
  error?: unknown;
}

// ════════════════════════════════════════════════════════════════════
// Internal helpers
// ════════════════════════════════════════════════════════════════════

/**
 * Formatiert einen strukturierten Error-Code mit i18n.
 * Ersetzt `{key}`-Platzhalter mit Werten aus dem error-Objekt.
 *
 * @returns Übersetzte Message oder `null`, wenn der Error keinen Code hat,
 *          keine i18n-Funktion gegeben wurde oder es zum Code keinen Text
 *          gibt (Datenbank-Fehlercode wie '42501').
 */
function formatStructuredError(
  errObj: ApiError | null | undefined,
  t: TranslateFn | null | undefined,
): string | null {
  if (!errObj || !('code' in errObj) || !errObj.code || !t) return null;
  let msg = t(errObj.code);
  // Kein Text zu diesem Code → kein App-eigener Fehler, sondern ein
  // Datenbankfehler (PostgrestError hat ebenfalls `code`, z. B. '42501').
  // `t()` gibt bei unbekanntem Schlüssel den Schlüssel selbst zurück — bis
  // 08.10.2026 landete deshalb die nackte Zahl „42501" im Toast. Jetzt
  // übernimmt der Aufrufer mit dem generischen Text `hcApiFehler`.
  if (msg === errObj.code) return null;
  // Platzhalter ersetzen: {maxMb}, {name} etc.
  // split/join statt replace, weil:
  //   1. replace(string, ...) ersetzt nur das erste Vorkommen
  //   2. Wenn val zufällig {key2}-Pattern enthält, würde eine spätere Iter
  //      das mit-ersetzen (Templating-Injection bei User-kontrolliertem val).
  for (const key of Object.keys(errObj)) {
    if (key === 'code' || key === 'message') continue;
    const val = (errObj as Record<string, unknown>)[key];
    const placeholder = '{' + key + '}';
    msg = msg.split(placeholder).join(String(val));
  }
  return msg;
}

// ════════════════════════════════════════════════════════════════════
// Public API
// ════════════════════════════════════════════════════════════════════

/**
 * Wrapper für `db.*`-Aufrufe mit konsistentem Error-Handling.
 *
 * @param promise   - Der `db.*`-Aufruf (muss `{data, error}`-Shape liefern)
 * @param openToast - Optional: Toast-Funktion für UI-Feedback
 * @param t         - Optional: i18n-Funktion für übersetzte Meldungen
 * @param context   - Optional: Kontext für Logging (z.B. `'createAuftrag'`)
 * @returns Daten bei Erfolg, `null` bei Fehler (Fehler wurde bereits geloggt)
 */
export async function api<T>(
  promise: Promise<ApiResult<T>>,
  openToast?: ToastFn | null,
  t?: TranslateFn | null,
  context?: string,
): Promise<T | null> {
  try {
    const res = await promise;

    // Error-Handling
    // Explicit null/undefined check statt `if (res.error)`, weil
    // `res.error === 0`, `''` oder `false` zwar selten sind, aber falsy
    // sind und sonst stillschweigend als "kein Error" durchrutschen würden.
    if (res && res.error !== undefined && res.error !== null) {
      // Narrow unknown error to object for property checks
      const errObj =
        typeof res.error === 'object' && res.error !== null
          ? (res.error as Record<string, unknown>)
          : null;

      // Strukturierter Error-Code (z.B. Upload-Validation)
      const structuredMsg = errObj
        ? formatStructuredError(errObj as ApiError, t)
        : null;
      if (structuredMsg) {
        const code = errObj && 'code' in errObj ? String(errObj.code) : '';
        logError({ message: code }, context || 'api');
        if (openToast) openToast(structuredMsg, 'error');
        return null;
      }
      // Supabase-Error: { error: { message: '...' } }
      const errMsg =
        errObj && 'message' in errObj ? String(errObj.message) : null;
      const msg = errMsg || (typeof res.error === 'string' ? res.error : 'Unknown error');
      logError({ message: String(msg) }, context || 'api');
      if (openToast) {
        const userMsg = t ? t('hcApiFehler') : 'Fehler bei der Datenverarbeitung';
        openToast(userMsg, 'error');
      }
      return null;
    }

    // Erfolg: Daten zurückgeben
    return res.data ?? null;
  } catch (err: unknown) {
    // Netzwerk-/Runtime-Fehler
    logError(err, context || 'api');
    if (openToast) {
      const userMsg = t ? t('hcNetzwerkFehler') : 'Netzwerkfehler — bitte erneut versuchen';
      openToast(userMsg, 'error');
    }
    return null;
  }
}

/**
 * Wie {@link api}, aber ohne UI-Feedback (für Hintergrund-Operationen).
 */
export async function apiSilent<T>(
  promise: Promise<ApiResult<T>>,
  context?: string,
): Promise<T | null> {
  return api<T>(promise, null, null, context);
}

// ════════════════════════════════════════════════════════════════════
// apiCall — sicherer Wrapper mit diskriminierter Union
// ════════════════════════════════════════════════════════════════════

/**
 * Erfolgs-Ergebnis von {@link apiCall}. `data` ist bei `void`-artigen
 * Operationen (Delete, Update ohne Return) als `null` definiert.
 */
export interface ApiOk<T> {
  ok: true;
  data: T | null;
}

/** Fehler-Ergebnis von {@link apiCall} — Fehler wurde bereits geloggt/getoastet. */
export interface ApiFail {
  ok: false;
}

/**
 * Diskriminierte Union: entweder Erfolg (mit oder ohne Daten) oder Fehler.
 *
 * Verwendung:
 *
 *     const res = await apiCall(db.deleteAuftrag(id), openToast, t, 'deleteAuftrag');
 *     if (!res.ok) return;
 *     // Hier: ok === true. Bei Funktionen ohne data ist res.data === null.
 *     setAuftraege((prev) => prev.filter((a) => a.id !== id));
 */
export type ApiOutcome<T> = ApiOk<T> | ApiFail;

/**
 * Wie {@link api}, aber liefert eine diskriminierte Union zurück.
 *
 * HINTERGRUND
 * ===========
 * Der ältere {@link api}-Wrapper returnt bei Erfolg `data ?? null` — was
 * für `db.*`-Funktionen, die nur `{ error }` zurückgeben (delete*, update*),
 * **immer `null`** ergibt. Caller, die dann mit `if (!res) return;` prüfen,
 * springen auch bei **erfolgreichem** Lösch-/Update-Vorgang in den Frühzeitig-
 * Return — UI wird nicht aktualisiert.
 *
 * `apiCall` löst das, indem es Erfolg und Misserfolg explizit als Union
 * trennt: `{ ok: true, data: T|null }` vs `{ ok: false }`. So funktioniert
 * `if (!res.ok) return;` auch für Operationen ohne Rückgabedaten korrekt.
 *
 * Audit 088 (17.05.2026): Eingeführt nach Bug-Report „AdminBewertungen
 * löschen wirkt erst nach Reload" + systematischem Audit, der das gleiche
 * Antipattern in 11 Stellen aufgedeckt hat. Migration erfolgt schrittweise —
 * `api()` und `apiSilent()` bleiben abwärtskompatibel.
 */
export async function apiCall<T>(
  promise: Promise<ApiResult<T>>,
  openToast?: ToastFn | null,
  t?: TranslateFn | null,
  context?: string,
): Promise<ApiOutcome<T>> {
  try {
    const res = await promise;

    // Identisches Error-Handling wie in api() — bewusst dupliziert statt
    // gemeinsamen Helper, weil die Rückgabe-Semantik unterschiedlich ist.
    if (res && res.error !== undefined && res.error !== null) {
      const errObj =
        typeof res.error === 'object' && res.error !== null
          ? (res.error as Record<string, unknown>)
          : null;

      const structuredMsg = errObj
        ? formatStructuredError(errObj as ApiError, t)
        : null;
      if (structuredMsg) {
        const code = errObj && 'code' in errObj ? String(errObj.code) : '';
        logError({ message: code }, context || 'apiCall');
        if (openToast) openToast(structuredMsg, 'error');
        return { ok: false };
      }
      const errMsg =
        errObj && 'message' in errObj ? String(errObj.message) : null;
      const msg = errMsg || (typeof res.error === 'string' ? res.error : 'Unknown error');
      logError({ message: String(msg) }, context || 'apiCall');
      if (openToast) {
        const userMsg = t ? t('hcApiFehler') : 'Fehler bei der Datenverarbeitung';
        openToast(userMsg, 'error');
      }
      return { ok: false };
    }

    // Erfolg — auch wenn data undefined ist (Delete/Update ohne Return)
    return { ok: true, data: res?.data ?? null };
  } catch (err: unknown) {
    logError(err, context || 'apiCall');
    if (openToast) {
      const userMsg = t ? t('hcNetzwerkFehler') : 'Netzwerkfehler — bitte erneut versuchen';
      openToast(userMsg, 'error');
    }
    return { ok: false };
  }
}

/**
 * Wie {@link apiCall}, aber ohne UI-Feedback (für Hintergrund-Operationen).
 *
 * Verwendung wie `apiCall`, aber ohne Toast bei Fehler — die Fehler werden
 * nur via `logError()` ins Monitoring geschrieben. Geeignet für Background-
 * Syncs, automatische Reloads, optimistische Updates mit stillem Rollback.
 */
export async function apiCallSilent<T>(
  promise: Promise<ApiResult<T>>,
  context?: string,
): Promise<ApiOutcome<T>> {
  return apiCall<T>(promise, null, null, context);
}

/**
 * Prüft ob ein `MutationResult` aus einem useFahrerData-Hook (removeAuftrag,
 * removeKunde, removeRechnung, removeBuchung, removeFahrt) einen Fehler
 * signalisiert.
 *
 * Hintergrund: die Hook-Funktionen returnen entweder das eigentliche
 * Result-Objekt (Erfolg) oder `{ error: true }` (Misserfolg) — eine
 * schwach typisierte Discriminated Union (`MutationResult = { error: boolean } | unknown`).
 * Dieser Helper kapselt den safe runtime-Check, sodass Caller einheitlich
 * arbeiten können:
 *
 *   const res = await removeAuftrag(id);
 *   if (isMutationError(res)) {
 *     openToast(t('errAllgemein'), 'error');
 *     return;
 *   }
 *   // Erfolg → Hook hat das State bereits aktualisiert
 *
 * Pattern entstand aus dem Audit-Sprint vom 28-29.04.2026 (Bug 66 + Folge-
 * audit der remove*-Aufrufer).
 */
export function isMutationError(res: unknown): boolean {
  return (
    !!res &&
    typeof res === 'object' &&
    (res as { error?: unknown }).error === true
  );
}

